import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useStore } from '@hub/helpers/use-store';
import { Card, Col, Flex, Row } from '@monorepo/react-components';
import { useResponsiveness } from '@monorepo/react-hooks';
import {
  Account,
  AccountBalance as AccountBalanceType,
  AddOnType,
  Application,
} from '@monorepo/types';
import AddOns from '../../add-ons/add-ons';
import ClosedPositionsTable from '../../positions/closed-positions';
import OpenPositions from '../../positions/open-positions';
import AccountBalance from '../dashboard-common/account-balance/account-balance';
import AccountInfo from '../dashboard-common/account-info/account-info';
import AdvancedStatistics from '../dashboard-common/advanced-statistics/advanced-statistics';
import DailyPause from '../dashboard-common-new/progress-cards-ttp/daily-pause';
import MaxLoss from '../dashboard-common-new/progress-cards-ttp/max-loss';
import ProfitTargetTtp from '../dashboard-common-new/progress-cards-ttp/profit-target-ttp';
import AccountRulesTtp from './account-rules-ttp/account-rules-ttp';
import BestRatesCards from './best-rates-cards/best-rates-cards';

interface Props {
  account: Account;
}

const APPLICATION_TYPE = Application.TTP;

const TtpDashboardNew: FunctionComponent<Props> = ({ account }) => {
  const {
    dataStore: { accountStore, programStore },
  } = useStore();
  const { isDesktop, isMobile, isDesktopXxl } = useResponsiveness();

  const [accountDetails, setAccountDetails] = useState<AccountBalanceType>({
    profitAndLoss: 0,
    dailyProfitAndLoss: 0,
    balance: 0,
    credits: 0,
    equity: 0,
    unrealized: 0,
    invalidProfitAndLoss: 0,
    dailyInvalidProfitAndLoss: 0,
    maxLoss: 0,
    baseMaxLoss: 0,
    withdrawals: [],
  });
  const id = account.tsAccountId as string;

  useEffect(() => {
    const getAccountBalance = async () => {
      try {
        const result = await accountStore.getBalance(id);

        setAccountDetails((prev) => ({ ...prev, ...result }));
      } catch (e) {
        console.error(`Failed getting account details`, e);
      }
    };

    getAccountBalance();
  }, [id]);

  const showDailyMetrics = programStore.hasDailyMetrics(account.programId);

  const program = programStore.get(account.programId);
  const programTarget = program?.config.target || 0;

  const baseBalance = Number(program?.config.buyingPower) || 0;

  const withdrawalAmount = useMemo(() => {
    const withdrawals = accountDetails.withdrawals || [];

    return withdrawals.reduce((sum, { chargedCredits, chargedAmount }) => {
      return sum + chargedAmount + chargedCredits;
    }, 0);
  }, [accountDetails]);

  return (
    <Flex gap={24} vertical className={'dashboard-new'}>
      <AddOns type={AddOnType.Renew} accountId={account.tsAccountId || ''} />
      <Row gutter={24}>
        <Col xs={24} xxl={17}>
          <Flex vertical gap={24}>
            {!isDesktop && <AccountInfo account={account} />}
            <div className="daily-card-container">
              <ProfitTargetTtp
                target={programTarget}
                base={baseBalance}
                profitAndLoss={accountDetails.profitAndLoss}
              />

              <MaxLoss
                maxLoss={accountDetails.maxLoss || 0}
                profitAndLoss={accountDetails.profitAndLoss}
                baseBalance={baseBalance}
              />
              {showDailyMetrics && (
                <DailyPause
                  accountId={account.tsAccountId || ''}
                  allowedDailyLosses={program?.config.dailyPause || 0}
                  dailyPnL={accountDetails.dailyProfitAndLoss}
                />
              )}
            </div>

            <Row gutter={[24, 24]}>
              <Col xs={24} lg={16} xxl={24}>
                <Flex vertical gap={24}>
                  <AccountBalance
                    equity={accountDetails.equity}
                    unrealized={accountDetails.unrealized}
                    balance={accountDetails.balance}
                    baseBalance={baseBalance}
                    profitAndLoss={accountDetails.profitAndLoss}
                    dailyProfitAndLoss={accountDetails.dailyProfitAndLoss}
                    accountId={id}
                    application={APPLICATION_TYPE}
                    invalidProfitAndLoss={
                      accountDetails.invalidProfitAndLoss || 0
                    }
                    dailyInvalidProfitAndLoss={
                      accountDetails.dailyInvalidProfitAndLoss
                    }
                    credits={accountDetails.credits}
                    withdrawalAmount={withdrawalAmount}
                  />
                  <BestRatesCards accountId={id} />
                  <OpenPositions
                    application={APPLICATION_TYPE}
                    hideSwap={true}
                    accountId={id}
                  />
                  <ClosedPositionsTable
                    application={APPLICATION_TYPE}
                    hideSwap={true}
                    accountId={id}
                  />
                </Flex>
              </Col>
              {!isDesktopXxl && (
                <Col xs={24} lg={8}>
                  <Card
                    className={classNames('side-stats', {
                      'side-stats--mobile': isMobile,
                      'side-stats--desktop': !isMobile,
                    })}
                  >
                    <Flex vertical gap={32}>
                      {isDesktop && <AccountInfo account={account} />}
                      <AccountRulesTtp account={account} />
                      <AdvancedStatistics
                        accountId={account.tsAccountId || ''}
                      />
                    </Flex>
                  </Card>
                </Col>
              )}
            </Row>
          </Flex>
        </Col>
        {isDesktopXxl && (
          <Col xs={24} lg={7}>
            <Card
              active
              className={classNames('side-stats', {
                'side-stats--mobile': isMobile,
                'side-stats--desktop': !isMobile,
              })}
            >
              <Flex vertical gap={32}>
                <AccountInfo account={account} />
                <AccountRulesTtp account={account} />
                <AdvancedStatistics accountId={account.tsAccountId || ''} />
              </Flex>
            </Card>
          </Col>
        )}
      </Row>
    </Flex>
  );
};

export default TtpDashboardNew;
